import React, { createContext, useContext, useState, useEffect } from 'react';

const SEOCtx = createContext();

// Default SEO data for every page
const DEFAULT_SEO = {
  '/': {
    title: 'Apnaa Calculator - India\'s #1 Free Financial Calculator',
    description: 'Free online financial calculators for India. EMI, SIP, FD, Income Tax, GST, HRA, EPF and 30+ more. Fast, accurate, made for Indians.',
    keywords: 'EMI calculator, SIP calculator, income tax calculator India, GST calculator, FD calculator, HRA calculator',
    h1: 'India\'s Best Free Financial Calculator',
    intro: 'Apnaa Calculator provides 30+ free financial calculators designed specifically for Indian users. Calculate EMI, SIP returns, income tax, GST, FD maturity and more.',
  },
  '/emi': {
    title: 'EMI Calculator - Calculate Loan EMI Online Free | Apnaa Calculator',
    description: 'Free EMI calculator India. Calculate Equated Monthly Installment for home loan, car loan, personal loan. Get instant EMI with interest and principal breakup.',
    keywords: 'EMI calculator, loan EMI calculator India, monthly installment calculator, EMI calculation formula',
    h1: 'EMI Calculator',
    intro: 'Calculate your Equated Monthly Installment (EMI) instantly. Enter loan amount, interest rate and tenure to get your monthly EMI along with total interest payable.',
  },
  '/home-loan': {
    title: 'Home Loan EMI Calculator 2024 | Apnaa Calculator',
    description: 'Calculate home loan EMI online free. Get accurate monthly installment for any home loan amount, interest rate and tenure. Compare different loan scenarios.',
    keywords: 'home loan EMI calculator, housing loan calculator India, home loan monthly installment, housing loan EMI 2024',
    h1: 'Home Loan EMI Calculator',
    intro: 'Plan your home purchase with our Home Loan EMI Calculator. Know your exact monthly installment, total interest payable and principal repayment schedule.',
  },
  '/sip': {
    title: 'SIP Calculator - Calculate SIP Returns Online | Apnaa Calculator',
    description: 'Free SIP calculator to estimate returns on your Systematic Investment Plan. Calculate maturity amount, wealth gained and monthly SIP needed to reach your goal.',
    keywords: 'SIP calculator, SIP return calculator, mutual fund SIP calculator India, systematic investment plan calculator',
    h1: 'SIP Calculator',
    intro: 'Calculate how much your monthly SIP investments will grow over time. Our SIP calculator uses compound interest to show your expected maturity value.',
  },
  '/income-tax': {
    title: 'Income Tax Calculator FY 2024-25 | New vs Old Regime | Apnaa Calculator',
    description: 'Calculate income tax for FY 2024-25. Compare new tax regime vs old tax regime. Find which regime saves you more tax. Includes 80C, 80D, HRA deductions.',
    keywords: 'income tax calculator 2024-25, new tax regime calculator, old tax regime calculator, income tax India',
    h1: 'Income Tax Calculator FY 2024-25',
    intro: 'Calculate your income tax liability for FY 2024-25 under both New and Old tax regimes. Our calculator automatically recommends the regime that saves you more tax.',
  },
  '/gst': {
    title: 'GST Calculator - Add or Remove GST Online | Apnaa Calculator',
    description: 'Free GST calculator India. Calculate GST on any amount for 0%, 5%, 12%, 18%, 28% tax rates. Get CGST, SGST and IGST breakup instantly.',
    keywords: 'GST calculator India, GST calculation online, CGST SGST calculator, GST inclusive exclusive calculator',
    h1: 'GST Calculator',
    intro: 'Calculate GST amount instantly. Whether you need to add GST to a price or remove GST from an inclusive amount, our calculator handles both cases with full CGST/SGST breakup.',
  },
};

// Generate default for pages not explicitly listed
function getDefault(path) {
  const name = path.replace('/', '').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  return {
    title: `${name} - Free Online Calculator | Apnaa Calculator`,
    description: `Free ${name} for India. Fast, accurate calculation with detailed results.`,
    keywords: `${name.toLowerCase()}, calculator India`,
    h1: name,
    intro: `Use our free ${name} to get accurate results instantly.`,
  };
}

export function SEOProvider({ children }) {
  const [seoData, setSeoData] = useState(() => {
    try {
      const saved = localStorage.getItem('apnaa_seo');
      return saved ? { ...DEFAULT_SEO, ...JSON.parse(saved) } : DEFAULT_SEO;
    } catch { return DEFAULT_SEO; }
  });

  const getSEO = (path) => seoData[path] || getDefault(path);

  const updateSEO = (path, data) => {
    const updated = { ...seoData, [path]: { ...getSEO(path), ...data } };
    setSeoData(updated);
    localStorage.setItem('apnaa_seo', JSON.stringify(updated));
  };

  const resetSEO = (path) => {
    const updated = { ...seoData };
    delete updated[path];
    setSeoData(updated);
    const stored = JSON.parse(localStorage.getItem('apnaa_seo') || '{}');
    delete stored[path];
    localStorage.setItem('apnaa_seo', JSON.stringify(stored));
  };

  const resetAll = () => {
    setSeoData(DEFAULT_SEO);
    localStorage.removeItem('apnaa_seo');
  };

  return (
    <SEOCtx.Provider value={{ getSEO, updateSEO, resetSEO, resetAll, allPaths: Object.keys(DEFAULT_SEO) }}>
      {children}
    </SEOCtx.Provider>
  );
}

export const useSEO = () => useContext(SEOCtx);
export { DEFAULT_SEO };
